import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { MapPin, Filter } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';

interface QuietSpotsMapProps {
  mapboxToken?: string;
}

const QuietSpotsMap: React.FC<QuietSpotsMapProps> = ({ mapboxToken }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const token = mapboxToken || 'pk.eyJ1IjoiemFpbmg0MiIsImEiOiJjbTR1NHBoYjMwZXQ4MmtzYWIwZzJzaWR6In0.gE5QluhHA_G_TzvGpOsRdg';

  const categories = [
    { value: 'all', label: 'All Spots' },
    { value: 'cafe', label: 'Cafés' },
    { value: 'hotel', label: 'Hotel Lobbies' },
    { value: 'library', label: 'Libraries' },
    { value: 'coworking', label: 'Coworking Spaces' },
    { value: 'cultural', label: 'Cultural Spaces' },
    { value: 'other', label: 'Other' }
  ];

  const quietSpots = [
    { id: 1, name: "The Hoxton, Southwark (Lobby)", address: "40 Blackfriars Rd, SE1 8NY", type: "Hotel Lobby", category: "hotel", description: "Comfortable lobby space for work" },
    { id: 2, name: "Working From_ (The Hoxton, Southwark)", address: "32 Blackfriars Road, SE1 8PB", type: "Dedicated Coworking Space", category: "coworking", description: "Professional coworking environment" },
    { id: 3, name: "The Standard, London", address: "10 Argyle Street, WC1H 8EG", type: "Hotel Library Lounge", category: "hotel", description: "Quiet library lounge space" },
    { id: 4, name: "citizenM London Bankside", address: "citizenM London Bankside", type: "Southwark", category: "hotel", description: "Modern hotel lobby area" },
    { id: 5, name: "Nhow London", address: "2 Macclesfield Road, EC1V 8DG", type: "Hotel Lobby Coworking / Private Lab", category: "hotel", description: "Contemporary workspace in hotel" },
    { id: 6, name: "TRIBE London Canary Wharf", address: "15 Water St, E14 5GX", type: "Hotel Social Hub (Coworking)", category: "hotel", description: "Social hub for working and meetings" },
    { id: 7, name: "Bermonds Locke", address: "157 Tower Bridge Road, SE1 3LW", type: "Aparthotel Coworking Space / Cafe", category: "coworking", description: "Aparthotel with dedicated workspace" },
    { id: 8, name: "Leman Locke", address: "15 Leman Street, E1 8EN", type: "Aparthotel Coworking Space / Cafe", category: "coworking", description: "Modern aparthotel workspace" },
    { id: 9, name: "Buckle Street Studios by Locke", address: "21 Buckle Street, E1 8DB", type: "Aparthotel Mezzanine Coworking", category: "coworking", description: "Stylish mezzanine workspace" },
    { id: 10, name: "Ember Locke", address: "202-220 Cromwell Road, SW5 0SW", type: "Aparthotel Coworking Space / Cafe", category: "coworking", description: "Comfortable coworking cafe space" },
    { id: 11, name: "Locke at Broken Wharf", address: "Broken Wharf House, 2 Broken Wharf, EC4V 3DT", type: "Aparthotel Coworking Space", category: "coworking", description: "Professional workspace environment" },
    { id: 12, name: "Mama Shelter London Shoreditch", address: "437 Hackney Road, E2 8PP", type: "Hotel Lobby / Ateliers (Meeting Rooms)", category: "hotel", description: "Creative meeting spaces and lobby" },
    { id: 13, name: "One Hundred Shoreditch", address: "100 Shoreditch High Street, E1 6JQ", type: "Hotel Lobby Bar / Meeting Rooms", category: "hotel", description: "Vibrant lobby and meeting spaces" },
    { id: 14, name: "Fuckoffee", address: "Unit A, 163 Bermondsey Street, SE1 3UW", type: "Independent Cafe", category: "cafe", description: "Unique independent cafe space" },
    { id: 15, name: "Clifton Nurseries Cafe", address: "5A Clifton Villas, W9 2PH", type: "Garden Cafe", category: "cafe", description: "Peaceful garden cafe setting" },
    { id: 16, name: "Gecko Coffeehouse", address: "49 Bethnal Green Road, E1 6LA", type: "Independent Vegan Cafe", category: "cafe", description: "Plant-based cafe with workspace" },
    { id: 17, name: "Prufrock Coffee", address: "23-25 Leather Lane, EC1N 7TE", type: "Specialty Cafe", category: "cafe", description: "Premium coffee and workspace" },
    { id: 18, name: "Grind (Shoreditch example)", address: "213 Old Street, EC1V 9NR", type: "Cafe & Bar Chain", category: "cafe", description: "Popular coffee chain location" },
    { id: 19, name: "Mare Street Market (Hackney)", address: "117 Mare Street, E8 4RU", type: "Concept Store & Cafe", category: "cafe", description: "Creative market and cafe space" },
    { id: 20, name: "Mare Street Market (King's Cross)", address: "2 Lewis Cubitt Square, N1C 4DY", type: "Concept Store & Cafe", category: "cafe", description: "Modern concept store with cafe" },
    { id: 21, name: "Redemption Roasters (Covent Garden)", address: "40 Drury Lane, WC2B 5RR", type: "Social Enterprise Cafe", category: "cafe", description: "Social impact coffee house" },
    { id: 22, name: "The British Library", address: "96 Euston Road, NW1 2DB", type: "National Library (Reading Rooms/Public)", category: "library", description: "Quiet reading rooms and study spaces" },
    { id: 23, name: "Wellcome Collection Library", address: "183 Euston Road, NW1 2BE", type: "Museum Library", category: "library", description: "Specialized museum library space" },
    { id: 24, name: "Barbican Library", address: "Level 2, Barbican Centre, Silk Street, EC2Y 8DS", type: "Public Library", category: "library", description: "Modern public library in cultural center" },
    { id: 25, name: "Bishopsgate Library", address: "230 Bishopsgate, EC2M 4QH", type: "Public Access Library", category: "library", description: "Historic library with public access" },
    { id: 26, name: "Peckham Library", address: "122 Peckham Hill Street, SE15 5JR", type: "Public Library", category: "library", description: "Award-winning public library" },
    { id: 27, name: "Southbank Centre", address: "Belvedere Road, SE1 8XX / The Queen's Walk, SE1 8XT", type: "Cultural Hub Foyers/Cafe", category: "cultural", description: "Cultural center with quiet spaces" },
    { id: 28, name: "Barbican Centre (Foyers/Cafes)", address: "Silk Street, EC2Y 8DS", type: "Cultural Hub Foyers/Cafes", category: "cultural", description: "Cultural center foyers and cafes" },
    { id: 29, name: "St Pancras Int'l \"WorkStation\"", address: "Euston Road, N1C 4QP", type: "Train Station (Designated Cafes for Season Ticket Holders)", category: "other", description: "Designated work areas in train station" },
    { id: 30, name: "Everyman Cinemas (Lobby/Cafe)", address: "Check specific venue", type: "Cinema Lobby/Cafe", category: "other", description: "Cinema lobby and cafe spaces" },
    { id: 31, name: "Two More Years", address: "7 Roach Rd, Fish Island, E3 2PA", type: "Bar, Restaurant, Coworking", category: "coworking", description: "Multi-purpose venue with coworking" },
    { id: 32, name: "Eastern Curve Garden", address: "13 Dalston Lane, E8 3DF", type: "Outdoor Garden", category: "other", description: "Peaceful outdoor garden space" },
    { id: 33, name: "Google Campus London", address: "Bonhill Street/Old Street \"Silicon Roundabout\" area", type: "Former Public Coworking Space", category: "coworking", description: "Former tech campus space" },
    { id: 34, name: "Coffee Garden Leytonstone", address: "Leytonstone, London", type: "Garden Cafe", category: "cafe", description: "Peaceful garden cafe with outdoor seating" }
  ];

  const [approvedSpots, setApprovedSpots] = useState<any[]>([]);

  useEffect(() => {
    const loadApprovedSpots = () => {
      const approved = JSON.parse(localStorage.getItem('approvedSpots') || '[]');
      setApprovedSpots(approved);
    };
    loadApprovedSpots();
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'approvedSpots') loadApprovedSpots();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  type SpotType = {
    id: number | string;
    name: string;
    address: string;
    type: string;
    category: string;
    description: string;
    isApproved?: boolean;
  };

  const allSpots: SpotType[] = [
    ...quietSpots.map(spot => ({ ...spot, isApproved: false })),
    ...approvedSpots.map(spot => ({
      id: `approved-${spot.id}`,
      name: spot.name,
      address: spot.address,
      type: spot.types?.[0] ? spot.types[0].replace('_', ' ') + ' (Community)' : 'Community Spot',
      category: spot.types?.includes('cafe') ? 'cafe' : 
                spot.types?.includes('library') ? 'library' :
                spot.types?.includes('lodging') ? 'hotel' : 'other',
      description: `Community suggested spot - ${spot.types?.[0]?.replace('_', ' ') || 'location'}`,
      isApproved: true
    }))
  ];

  const filteredSpots = selectedCategory === 'all' 
    ? allSpots 
    : allSpots.filter(spot => spot.category === selectedCategory);

  const generateGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  };

  const searchLocation = async (query: string) => {
    if (!query.trim()) return;
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${token}&country=GB&limit=1`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        map.current?.flyTo({ center: [lng, lat], zoom: 14, duration: 2000 });
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    searchLocation(searchQuery);
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        map.current?.flyTo({ center: [longitude, latitude], zoom: 15, duration: 2000 });
        setIsLocating(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to get your location. Please check your browser permissions.');
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  };

  const geocodeAndAddMarkers = async (spotsToShow = filteredSpots) => {
    if (!map.current) return;
    clearMarkers();

    for (const spot of spotsToShow) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(spot.address)}.json?access_token=${token}&country=GB&limit=1`
        );
        const data = await response.json();
        
        if (data.features && data.features.length > 0) {
          const [lng, lat] = data.features[0].center;
          
          const el = document.createElement('div');
          el.className = 'marker';
          el.style.backgroundColor = spot.isApproved ? 'hsl(142, 76%, 36%)' : 'hsl(158, 33%, 18%)';
          el.style.width = '20px';
          el.style.height = '20px';
          el.style.borderRadius = '50%';
          el.style.border = '3px solid white';
          el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
          el.style.cursor = 'pointer';

          const googleMapsUrl = generateGoogleMapsUrl(spot.address);

          const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(
            `<div style="padding: 12px; font-family: DM Sans, sans-serif;">
              <h3 style="font-weight: 600; color: ${spot.isApproved ? 'hsl(142, 76%, 36%)' : 'hsl(158, 33%, 18%)'}; margin-bottom: 4px; font-size: 14px;">${spot.name}${spot.isApproved ? ' ✨' : ''}</h3>
              <p style="font-size: 13px; color: hsl(158, 20%, 40%); margin-bottom: 4px;">${spot.type}</p>
              <p style="font-size: 12px; color: hsl(158, 20%, 55%); margin-bottom: 8px;">${spot.description}</p>
              <p style="font-size: 11px; color: hsl(158, 20%, 60%); margin-bottom: 12px;">${spot.address}</p>
              <a 
                href="${googleMapsUrl}" 
                target="_blank" 
                rel="noopener noreferrer"
                style="display: inline-flex; align-items: center; gap: 4px; padding: 6px 12px; background-color: hsl(158, 33%, 18%); color: white; font-size: 12px; border-radius: 9999px; text-decoration: none; transition: opacity 0.2s;"
                onmouseover="this.style.opacity='0.9'"
                onmouseout="this.style.opacity='1'"
              >
                Open in Google Maps
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15,3 21,3 21,9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </a>
            </div>`
          );

          const marker = new mapboxgl.Marker(el)
            .setLngLat([lng, lat])
            .setPopup(popup)
            .addTo(map.current!);

          markersRef.current.push(marker);
        }
      } catch (error) {
        console.error(`Error geocoding ${spot.name}:`, error);
      }
    }
  };

  useEffect(() => {
    if (map.current) {
      geocodeAndAddMarkers(filteredSpots);
    }
  }, [selectedCategory, approvedSpots]);

  useEffect(() => {
    if (!mapContainer.current || !token) return;

    mapboxgl.accessToken = token;
    
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-0.0935, 51.5164],
      zoom: 12
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      geocodeAndAddMarkers();
    });

    return () => {
      clearMarkers();
      map.current?.remove();
    };
  }, [token]);

  return (
    <div className="w-full space-y-4">
      {/* Google Maps link */}
      <div className="text-center mb-4">
        <p className="text-muted-foreground text-sm">
          <a
            href="https://www.google.com/maps/d/u/0/edit?mid=19WuVL4Y21UVQ2ho85t_yKtZaYG6h6uw&usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground hover:opacity-80 underline transition-opacity duration-200"
          >
            View on Google Maps
          </a>
        </p>
      </div>

      {/* Category Filter */}
      <div className="flex items-center gap-3 mb-4">
        <Filter size={20} className="text-foreground" />
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
        >
          {categories.map((category) => (
            <option key={category.value} value={category.value}>
              {category.label}
            </option>
          ))}
        </select>
        <span className="text-sm text-muted-foreground">
          Showing {filteredSpots.length} of {allSpots.length} spots
        </span>
      </div>

      {/* Search Box with GPS Button */}
      <form onSubmit={handleSearchSubmit} className="flex gap-3">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Postcode (E14 7QB)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2.5 border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={isLocating}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-foreground hover:opacity-80 transition-opacity disabled:opacity-50"
            title="Use current location"
          >
            <MapPin size={20} className={isLocating ? 'animate-pulse' : ''} />
          </button>
        </div>
        <button
          type="submit"
          disabled={isSearching}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-full hover:opacity-90 transition-opacity disabled:opacity-50 font-medium text-sm"
        >
          {isSearching ? 'Searching...' : 'Search'}
        </button>
      </form>

      {/* Map Container */}
      <div className="w-full h-[600px] rounded-2xl overflow-hidden shadow-lg border border-border">
        <div ref={mapContainer} className="w-full h-full" />
      </div>
    </div>
  );
};

export default QuietSpotsMap;
